import { toast } from 'react-toastify';
import { useEffect, useState } from 'react';
import { Form, Input, Button, Upload } from 'antd';
import { UploadOutlined } from '@ant-design/icons';
import { createNewsItem, updateNewsItem } from '../../services/api';

const NewsForm = ({ initialValues, onSuccess, onCancel }) => {
  const [form] = Form.useForm();
  const [fileList, setFileList] = useState([]);
  const [uploading, setUploading] = useState(false);

  useEffect(() => {
    if (initialValues) {
      form.setFieldsValue(initialValues);
      if (initialValues.imageUrl) {
        // Handle both relative and absolute URLs
        const imageUrl = initialValues.imageUrl.startsWith('http')
          ? initialValues.imageUrl
          : `${process.env.REACT_APP_API_URL}${initialValues.imageUrl}`;

        setFileList([{
          uid: '-1',
          name: 'Current Image',
          status: 'done',
          url: imageUrl,
        }]);
      }
    }
  }, [initialValues, form]);

  const onFinish = async (values) => {
    try {
      setUploading(true);

      // Validate file for new items
      if (!initialValues?.id && !fileList[0]) {
        toast.error('Please select an image file');
        setUploading(false);
        return;
      }

      const formData = new FormData();

      // Append file first if it exists
      const file = fileList[0];
      if (file?.originFileObj) {
        formData.append('image', file.originFileObj);
      } else if (file?.uid && !file?.url) {
        // If we have a uid but no url, it's a new file that needs to be uploaded
        formData.append('image', file);
      }

      // Then append other fields
      formData.append('title', values.title);
      formData.append('content', values.content);

      if (initialValues?.id) {
        await updateNewsItem(initialValues.id, formData);
        toast.success('News updated successfully');
      } else {
        await createNewsItem(formData);
        toast.success('News created successfully');
      }
      form.resetFields();
      setFileList([]);
      onSuccess();
    } catch (error) {
      console.error('Error:', error);
      toast.error(error.response?.data?.message || 'Failed to save news');
    } finally {
      setUploading(false);
    }
  };

  const uploadProps = {
    onRemove: () => {
      setFileList([]);
    },
    beforeUpload: (file) => {
      if (!file.type.startsWith('image/')) {
        toast.error('You can only upload image files!');
        return false;
      }
      const isLt5M = file.size / 1024 / 1024 < 5;
      if (!isLt5M) {
        toast.error('Image must be smaller than 5MB!');
        return false;
      }
      setFileList([{ originFileObj: file }]);
      return false;
    },
    fileList,
  };

  return (
    <Form
      form={form}
      layout="vertical"
      onFinish={onFinish}
      encType="multipart/form-data"
    >
      <Form.Item
        name="title"
        label="Title"
        rules={[
          { required: true, message: 'Please input title!' },
          { max: 100, message: 'Title cannot exceed 100 characters' }
        ]}
      >
        <Input maxLength={100} showCount />
      </Form.Item>

      <Form.Item
        name="content"
        label="Content"
        rules={[
          { required: true, message: 'Please input content!' },
          { max: 2000, message: 'Content cannot exceed 2000 characters' }
        ]}
      >
        <Input.TextArea rows={4} maxLength={2000} showCount autoSize={{ minRows: 4, maxRows: 10 }} />
      </Form.Item>

      <Form.Item
        label="Image"
        required
        rules={[{
          validator: () => {
            if (!fileList[0] && !initialValues?.imageUrl) {
              return Promise.reject('Please upload an image!');
            }
            return Promise.resolve();
          }
        }]}
      >
        <Upload {...uploadProps} listType="picture" maxCount={1}>
          <Button icon={<UploadOutlined />}>Select Image</Button>
        </Upload>
      </Form.Item>

      <Form.Item>
        <Button type="primary" htmlType="submit" loading={uploading}>
          {initialValues?.id ? 'Update' : 'Create'}
        </Button>
        <Button onClick={onCancel} style={{ marginLeft: 8 }}>
          Cancel
        </Button>
      </Form.Item>
    </Form>
  );
};

export default NewsForm;
